import { MoodEntry, Achievement, Challenge } from '../types'

const ACHIEVEMENTS_TEMPLATES: Omit<Achievement, 'unlocked' | 'unlockedAt' | 'isNew'>[] = [
  {
    id: 'first_entry',
    title: 'Primo Passo',
    description: 'Hai registrato il tuo primo umore!',
    type: 'entries',
    requirement: 1
  },
  {
    id: 'week_streak',
    title: 'Una Settimana Forte',
    description: 'Hai tracciato il tuo umore per 7 giorni consecutivi',
    type: 'streak',
    requirement: 7
  },
  {
    id: 'month_streak',
    title: 'Mese Perfetto',
    description: 'Hai tracciato il tuo umore per 30 giorni consecutivi',
    type: 'streak',
    requirement: 30
  },
  {
    id: 'hundred_entries',
    title: 'Centenario',
    description: 'Hai registrato 100 momenti della tua vita',
    type: 'entries',
    requirement: 100
  },
  {
    id: 'happy_week',
    title: 'Settimana Radiosa',
    description: 'Hai avuto umore 4+ per 7 giorni consecutivi',
    type: 'mood',
    requirement: 7
  },
  {
    id: 'mood_explorer',
    title: 'Esploratore Emotivo',
    description: 'Hai provato tutti i livelli di umore',
    type: 'special',
    requirement: 5
  },
  {
    id: 'consistent_tracker',
    title: 'Tracker Costante',
    description: 'Hai tracciato il tuo umore per 50 giorni',
    type: 'consistency',
    requirement: 50
  },
  {
    id: 'year_warrior',
    title: 'Guerriero dell\'Anno',
    description: 'Hai tracciato il tuo umore per 365 giorni',
    type: 'streak',
    requirement: 365
  }
]

const CHALLENGES_TEMPLATES: Omit<Challenge, 'progress' | 'active' | 'completed' | 'startDate' | 'endDate'>[] = [
  {
    id: 'daily_tracker',
    title: 'Tracker Quotidiano',
    description: 'Traccia il tuo umore per 7 giorni consecutivi',
    type: 'weekly',
    target: 7,
    reward: '🏆 Badge Costanza'
  },
  {
    id: 'mood_variety',
    title: 'Varietà Emotiva',
    description: 'Registra almeno 3 livelli di umore diversi questa settimana',
    type: 'weekly',
    target: 3,
    reward: '🌈 Badge Esploratore'
  },
  {
    id: 'positive_month',
    title: 'Mese Positivo',
    description: 'Mantieni una media di umore superiore a 3.5 questo mese',
    type: 'monthly',
    target: 35, // 3.5 * 10 for easier calculation
    reward: '✨ Badge Ottimista'
  },
  {
    id: 'note_writer',
    title: 'Scrittore di Emozioni',
    description: 'Aggiungi note a 5 entry questa settimana',
    type: 'weekly',
    target: 5,
    reward: '📝 Badge Narratore'
  }
]

export function initializeAchievements(): Achievement[] {
  return ACHIEVEMENTS_TEMPLATES.map(template => ({
    ...template,
    unlocked: false,
    isNew: false
  }))
}

export function initializeChallenges(): Challenge[] {
  const now = new Date()
  return CHALLENGES_TEMPLATES.map(template => ({
    ...template,
    progress: 0,
    active: true,
    completed: false,
    startDate: now.toISOString()
  }))
}

export function checkAchievements(entries: MoodEntry[], currentAchievements: Achievement[]): Achievement[] {
  const newAchievements: Achievement[] = []
  
  currentAchievements.forEach(achievement => {
    if (achievement.unlocked) return
    
    let shouldUnlock = false
    
    switch (achievement.id) {
      case 'first_entry':
        shouldUnlock = entries.length >= 1
        break
        
      case 'week_streak':
      case 'month_streak':
      case 'year_warrior':
        shouldUnlock = getCurrentStreak(entries) >= achievement.requirement
        break
        
      case 'hundred_entries':
      case 'consistent_tracker':
        shouldUnlock = entries.length >= achievement.requirement
        break
        
      case 'happy_week':
        shouldUnlock = getHappyStreak(entries) >= achievement.requirement
        break
        
      case 'mood_explorer':
        const uniqueMoods = new Set(entries.map(e => e.mood))
        shouldUnlock = uniqueMoods.size >= 5
        break
    }
    
    if (shouldUnlock) {
      const unlockedAchievement: Achievement = {
        ...achievement,
        unlocked: true,
        unlockedAt: new Date().toISOString(),
        isNew: true
      }
      newAchievements.push(unlockedAchievement)
    }
  })
  
  return newAchievements
}

export function checkChallenges(entries: MoodEntry[], currentChallenges: Challenge[]): Challenge[] {
  return currentChallenges.map(challenge => {
    if (challenge.completed || !challenge.active) return challenge
    
    let newProgress = challenge.progress
    
    switch (challenge.id) {
      case 'daily_tracker':
        newProgress = getCurrentStreak(entries)
        break
        
      case 'mood_variety':
        const thisWeekEntries = getThisWeekEntries(entries)
        const uniqueMoods = new Set(thisWeekEntries.map(e => e.mood))
        newProgress = uniqueMoods.size
        break
        
      case 'positive_month':
        const thisMonthEntries = getThisMonthEntries(entries)
        if (thisMonthEntries.length > 0) {
          const avgMood = thisMonthEntries.reduce((sum, e) => sum + e.mood, 0) / thisMonthEntries.length
          newProgress = Math.floor(avgMood * 10) // Convert to integer for easier comparison
        }
        break
        
      case 'note_writer':
        const thisWeekEntriesWithNotes = getThisWeekEntries(entries).filter(e => e.note.trim().length > 0)
        newProgress = thisWeekEntriesWithNotes.length
        break
    }
    
    const completed = newProgress >= challenge.target
    
    return {
      ...challenge,
      progress: newProgress,
      completed,
      endDate: completed ? new Date().toISOString() : challenge.endDate
    }
  })
}

function getCurrentStreak(entries: MoodEntry[]): number {
  if (entries.length === 0) return 0
  
  let streak = 0
  const today = new Date()
  
  for (let i = 0; i < 365; i++) {
    const checkDate = new Date(today)
    checkDate.setDate(today.getDate() - i)
    const hasEntry = entries.some(entry => 
      new Date(entry.date).toDateString() === checkDate.toDateString()
    )
    
    if (hasEntry) {
      streak++
    } else if (i > 0) {
      break
    }
  }
  
  return streak
}

function getHappyStreak(entries: MoodEntry[]): number {
  if (entries.length === 0) return 0
  
  let streak = 0
  let maxStreak = 0
  const sortedEntries = [...entries].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  
  for (const entry of sortedEntries) {
    if (entry.mood >= 4) {
      streak++
      maxStreak = Math.max(maxStreak, streak)
    } else {
      streak = 0
    }
  }
  
  return maxStreak
}

function getThisWeekEntries(entries: MoodEntry[]): MoodEntry[] {
  const now = new Date()
  const weekStart = new Date(now.setDate(now.getDate() - now.getDay()))
  weekStart.setHours(0, 0, 0, 0)
  
  return entries.filter(entry => new Date(entry.date) >= weekStart)
}

function getThisMonthEntries(entries: MoodEntry[]): MoodEntry[] {
  const now = new Date()
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
  
  return entries.filter(entry => new Date(entry.date) >= monthStart)
}